import { Size } from '../size/Size'
import { SearchFiltersQuery } from '@/services/search/SearchFiltersQuery'
import { Color } from '@/services/color/Color'
import { Technique } from '@/services/technique/Technique'
import { SearchFiltersTranslations } from '@/services/search/SearchFiltersTranslations'

interface Orientation { id: string }

export class SearchFilters {
  searchText: string = ''
  techniquesSelected: Array<Partial<Technique>> = []
  orientationsSelected: Orientation[] = []
  themesSelected: Array<{ id: string }> = []
  pricesSelected: { price_min: number | null, price_max: number | null } = {
    price_min: null,
    price_max: null
  }
  sizesSelected: Size[] = []
  colorSelected: Color | null = null
  sortType: string = 'artworks'
  page: number = 1

  //  Additional filters
  forSale: boolean = false
  framed: boolean = false
  readyToHang: boolean = false
  video: boolean = false

  constructor(data = {}) {
    Object.assign(this, data)
  }

  toQuery(): SearchFiltersQuery {
    const techs = this.techniquesSelected.length ? this.techniquesSelected.filter(t => !t.children).map(t => {
      return [t.slug, this.transformArrayToQuery(t.children, 'slug')].filter(i => !!i).join(',')
    }).filter(childId => !!childId).join(',') : undefined


    const orientations = this.transformArrayToQuery(this.orientationsSelected, 'id')
    const themes = this.transformArrayToQuery(this.themesSelected, 'id')
    const sizes = this.transformArrayToQuery(this.sizesSelected, 'size_code')

    // console.log("toQuery", this);

    return {
      keyword: this.searchText || undefined,
      techs,
      orientations,
      themes,
      framed: this.framed ? 'true' : undefined,
      ready_to_hang: this.readyToHang ? 'true' : undefined,
      for_sale: this.forSale ? 'true' : undefined,
      video: this.video ? 'true' : undefined,
      sort_by: this.sortType && this.sortType !== 'artworks' ? this.sortType : undefined,
      price_max: this.pricesSelected.price_max ? String(this.pricesSelected.price_max) : undefined,
      price_min: this.pricesSelected.price_min ? String(this.pricesSelected.price_min) : undefined,
      sizes,
      color: this.colorSelected ? this.colorSelected.color : undefined,
      page: this.page > 1 ? this.page : undefined,
    }
  }

  fromQuery(query: SearchFiltersQuery): this {
    console.log("fromQuery", query);
    this.searchText = query.keyword || ''
    this.techniquesSelected = this.mapArrayQueryToArray(query.techs, 'slug') as Array<Partial<Technique>>
    this.orientationsSelected = this.mapArrayQueryToArray(query.orientations, 'id') as Array<{ id: string }>
    this.themesSelected = this.mapArrayQueryToArray(query.themes, 'id') as Array<{ id: string }>
    this.pricesSelected.price_max = query.price_max ? Number(query.price_max) : null
    this.pricesSelected.price_min = query.price_min ? Number(query.price_min) : null
    this.sizesSelected = this.mapArrayQueryToArray(query.sizes, 'size_code').map(s => new Size(s))
    this.colorSelected = query.color ? new Color({ color: query.color }) : null
    this.sortType = query.sort_by || 'artworks'
    this.framed = query.framed ? Boolean(query.framed) : false
    this.readyToHang = query.ready_to_hang ? Boolean(query.ready_to_hang) : false
    this.forSale = query.for_sale ? Boolean(query.for_sale) : false
    this.video = query.video ? Boolean(query.video) : false

    if (query.page && Number(query.page) > 1) {
      this.page = Number(query.page);
    }

    return this
  }

  translate(translations: SearchFiltersTranslations): this {
    this.orientationsSelected = translations.orientations.filter(
      o => this.orientationsSelected.some(so => so.id === o.id)
    )

    this.themesSelected = translations.themes.filter(
      o => this.themesSelected.some(so => so.id === o.id)
    )

    this.sizesSelected = translations.sizes.filter(
      s => this.sizesSelected.some(ss => ss.size_code === s.size_code)
    )

    this.colorSelected = translations.colors.find(
      c => this.colorSelected && c.color === this.colorSelected.color
    ) || null

    /* const techniques = translations.techs.map(t => {
      return t.children
    }).flat().filter(childId => !!childId) as Array<Partial<Technique>> */

    // For suporting older safari (due to a lot of error event on bugsnag)
    // TODO use browserslist in package.json instead
    // Not using for now
    const techniques: Technique[] = [];
    translations.techs.forEach(t => {
      techniques.push(new Technique(t));
      if (t.children) {
        t.children.forEach(child => techniques.push(child));
      }
    })

    this.techniquesSelected = techniques.filter(
      s => this.techniquesSelected.some(ss => ss.slug === s.slug)
    ).sort((a, b) => a.slug.localeCompare(b.slug));

    return this
  }

  private transformArrayToQuery(items: any, byKey: string): string | undefined {
    if (!Array.isArray(items) || !items.length) return undefined
    return items.map(i => i[byKey]).join(',')
  }

  private mapArrayQueryToArray<T>(items: string | undefined, keyBy: string): Array<{ [key: string]: string }> {
    if (!items) return []
    const r = items.split(',').map(id => ({ [keyBy]: id }))
    return r
  }
}
